import {SectionItem} from 'common/components/items-menu/types';
import {
  BasicFunnelStep,
  DateKeyedFunnelSegments,
  FunnelStep,
  TrendChartType,
  TrendCountType,
} from 'common/report/funnels/models/types';
import {formatConciseDuration} from 'common/util/date';
import {IChartDataItem} from 'common/charts/visualization/line-chart/types';
import {commaizeNumber, formatMetricNumber} from 'common/util';
import {formatPercent} from 'common/report/funnels/util';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import lineSvg from 'common/assets/images/charts/line.svg';
import cumulativeLineSvg from 'common/assets/images/charts/cumulative-line.svg';
import {LiftType} from 'common/report/util/lift/types';

/** All trend chart view labels, labels used for display in the UI */
export const TrendChartTypeSelection: {[key in TrendChartType]: SectionItem} = {
  [TrendChartType.Conversion]: {
    label: `Conversion`,
    tooltip: {text: `Plot the conversion rate trend over time`, placement: Placement.Left},
  },
  [TrendChartType.TimeToConvert]: {
    label: `Time to convert`,
    tooltip: {text: `Plot the average time between selected steps over time`, placement: Placement.Left},
  },
  [TrendChartType.TopOfFunnel]: {
    label: `Top of funnel`,
    tooltip: {text: `Plot the count of funnel entries over time`, placement: Placement.Left},
  },
  [TrendChartType.BottomOfFunnel]: {
    label: `Bottom of funnel`,
    tooltip: {text: `Plot the count of ultimate funnel conversions over time`, placement: Placement.Left},
  },
};

export const TrendCountTypeSelection: {[key in TrendCountType]: SectionItem} = {
  [TrendCountType.Linear]: {
    label: `Linear`,
    icon: `analysis-linear`,
    tooltip: {
      'image-src': lineSvg,
      text: `Each time interval is calculated individually.`,
      placement: Placement.Left,
      size: DisplaySize.Small,
    },
  },
  [TrendCountType.Cumulative]: {
    label: `Cumulative`,
    icon: `analysis-cumulative`,
    tooltip: {
      'image-src': cumulativeLineSvg,
      text: `Each time interval is calculated by looking at all previous intervals up to and including that interval.`,
      placement: Placement.Left,
      size: DisplaySize.Small,
    },
  },
};

export enum TrendValueFormat {
  Default = `default`,
  ChartAxis = `chart_axis`,
  DataTableAverage = `average`,
}

/**
 * A bundle of pure functions for extracting trend values for funnels based on TrendChartType.
 * The functions are pure, this-less, and safe to destructure
 */
export interface TrendValueGetter {
  getTrendValue: (step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number) => number;
  getStep: (steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number) => BasicFunnelStep;
}

const conversionValueGetter: TrendValueGetter = {
  getTrendValue(step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number): number {
    let conversionRatio = 0;
    if (step) {
      conversionRatio = shouldShowOverallConversion(selectedStepIndex) ? step.overall_conv_ratio : step.step_conv_ratio;
    }
    return conversionRatio;
  },
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
};

const timeToConvertValueGetter: TrendValueGetter = {
  getTrendValue(step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number): number {
    let averageTime = 0;
    if (shouldShowOverallConversion(selectedStepIndex)) {
      averageTime = step.avg_time_from_start ?? 0;
    } else {
      averageTime = step.avg_time ?? 0;
    }
    return averageTime;
  },
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
};

const getFunnelTrendValue = (step: FunnelStep | BasicFunnelStep, _selectedStepIndex: null | number): number => {
  let count = 0;
  if (step) {
    count = step.count ? step.count : 0;
  }
  return count;
};

const topOfFunnelValueGetter: TrendValueGetter = {
  getTrendValue: getFunnelTrendValue,
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[0] : steps[selectedStepIndex];
  },
};

const bottomOfFunnelValueGetter: TrendValueGetter = {
  getTrendValue: getFunnelTrendValue,
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
};

export function shouldShowOverallConversion(eventIndex: number | null): boolean {
  return !Number.isInteger(eventIndex);
}

function formatSimpleValueType(value: number, format?: TrendValueFormat): string {
  if (format === TrendValueFormat.ChartAxis) {
    return formatMetricNumber(value);
  } else if (format === TrendValueFormat.DataTableAverage) {
    // 1 decimal place for average
    return Number.isInteger(value) ? `${commaizeNumber(value)}.0` : value.toFixed(1);
  } else {
    // commaize if not a float
    return Number.isInteger(value) ? commaizeNumber(value) : value.toString();
  }
}

/**
 * Returns the TrendValueGetter object corresponding to the TrendChartType that was passed in.
 * The TrendValueGetter object is used to return the appropriate value,
 * within the array of funnel steps provided, as well as the appropriate value representation
 * for each TrendChartType.
 *
 * @param  trendChartType  the trend chart (as a TrendChartType) for which to return its TrendValueGetter
 * @return the TrendValueGetter for the trendChartType specified
 */
export function getFunnelTrendValueGetter(trendChartType: TrendChartType | undefined): TrendValueGetter {
  const valueGetter: {[key in TrendChartType]: TrendValueGetter} = {
    [TrendChartType.Conversion]: conversionValueGetter,
    [TrendChartType.TimeToConvert]: timeToConvertValueGetter,
    [TrendChartType.TopOfFunnel]: topOfFunnelValueGetter,
    [TrendChartType.BottomOfFunnel]: bottomOfFunnelValueGetter,
  };
  return valueGetter[trendChartType];
}

export function formatTrendValue({
  value,
  trendChartType,
  formatType = TrendValueFormat.Default,
  liftType,
}: {
  value: number;
  trendChartType: TrendChartType;
  formatType?: TrendValueFormat;
  liftType?: LiftType;
}): string {
  const precision = formatType === TrendValueFormat.ChartAxis ? 1 : 2;

  if (liftType) {
    return formatPercent(value, precision) as string;
  }

  switch (trendChartType) {
    case TrendChartType.BottomOfFunnel:
    case TrendChartType.TopOfFunnel:
      return formatSimpleValueType(value, formatType);
      break;
    case TrendChartType.Conversion:
      // we want 2 digits for conversion rate, regardless
      return formatPercent(value, 2) as string;
      break;
    case TrendChartType.TimeToConvert:
      return formatConciseDuration(value, precision);
    default:
      return formatSimpleValueType(value, formatType);
      break;
  }
}

/**
 * Given several trend chart parameters, returns the chart data for a line chart
 * in a format understood by the mp-line-chart common component with params to format and style the
 * series on the line chart.
 * @param eventIndex index of the event/steps chosen for the chart/report
 * @param selectedSeriesColors a map of the segments selected to appear in the chart mapped as segment prop to segment color
 * @param orderedTrends an array of DateKeyedFunnelSegments, all the possible segments that could appear on the chart
 * @param trendTypeSelection a TrendChartType, the trend view selected for the chart
 * @param dashStyle the line style used when displaying the selected data on the chart
 */
export function getTrendChartData({
  eventIndex,
  selectedSeriesColors,
  orderedTrends,
  trendTypeSelection,
  dashStyle,
}: {
  eventIndex: null | number;
  selectedSeriesColors: Map<string, string>;
  orderedTrends: DateKeyedFunnelSegments[];
  trendTypeSelection: TrendChartType;
  dashStyle?: string;
}): IChartDataItem[] {
  return orderedTrends[0].segments.reduce((selectedSegments, funnelSegment, segmentIndex) => {
    if (!selectedSeriesColors.has(funnelSegment.prop)) {
      return selectedSegments;
    }
    const segmentData = orderedTrends.map(({date, segments}) => {
      const timestamp = date.getTime();
      const steps = segments[segmentIndex].steps;
      const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
      return [timestamp, trendValueGetter.getTrendValue(trendValueGetter.getStep(steps, eventIndex), eventIndex)];
    });
    selectedSegments.push({
      color: selectedSeriesColors.get(funnelSegment.prop),
      name: funnelSegment.propName,
      label: funnelSegment.prop,
      data: segmentData,
      dashStyle,
      visible: true,
    });
    return selectedSegments;
  }, []);
}
